import dayjs from 'dayjs';
import { ArrowUpRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Content,
  EndIcon,
  Hint,
  MutedTitle,
  RootLink,
  RootStatic,
  StartIcon,
  Subtitle,
  Title,
} from './RelatedLinkRow.styles';

export type RelatedLinkRowProps = {
  icon: ReactElement;
  title: string;
  href?: string;
  subtitle?: string;
  date?: string | Date;
  startTime?: string | Date;
  status?: string;
  /** See `RelatedLinkItem.disabledHint`. */
  disabledHint?: string;
};

export function RelatedLinkRow({
  icon,
  href,
  title,
  subtitle,
  date,
  startTime,
  status,
  disabledHint,
}: RelatedLinkRowProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const dateLabel = date
    ? `${dayjs(date).format('MMM D')}${
        startTime != null ? `, ${dayjs(startTime).format('HH:mm')}` : ''
      }`
    : null;
  const subtitleLabel =
    subtitle ??
    (dateLabel ? `Upcoming: ${dateLabel}` : null) ??
    (status ? t(`status_${status}`) : null);

  const content = (
    <>
      <StartIcon>{icon}</StartIcon>
      <Content>
        {disabledHint ? <MutedTitle>{title}</MutedTitle> : <Title>{title}</Title>}
        {subtitleLabel ? <Subtitle>{subtitleLabel}</Subtitle> : null}
        {disabledHint ? <Hint>{disabledHint}</Hint> : null}
      </Content>
      {href && !disabledHint ? (
        <EndIcon className='end-icon'>
          <ArrowUpRight size={16} />
        </EndIcon>
      ) : null}
    </>
  );

  // Not a link, and not focusable: arrow keys pass over it like any static row, and
  // the hint is part of its text.
  return href && !disabledHint ? (
    <RootLink to={href}>{content}</RootLink>
  ) : (
    <RootStatic>{content}</RootStatic>
  );
}
